import { FormControl, MenuItem, Select } from "@material-ui/core";
import clsx from "clsx";
import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Button/Button";
import { FieldErrorMessage } from "../FieldErrorMessage/FieldErrorMessage";
import { FieldLabel } from "../FieldLabel/FieldLabel";
import { FieldHelperText } from "../FieldHelperText/FieldHelperText";
import {
  mergeRefs,
  useFocusVisibleRef,
  useUniqueWcagValues,
} from "common-react";
import { DropdownSize } from "./tools/Dropdown.const";
import {
  DropdownOptionModel,
  DropdownRef,
  CustomDropdownProps,
} from "./tools/Dropdown.model";
import { DropdownIconIndicator } from "./components/DropdownIconIndicator";
import { DropdownDefaultValue } from "./components/DropdownDefaultValue";
import { Typography } from "../../Typography/Typography";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden";
import { mergeAriaDescribedBy } from "../../utils/ariaUtils";

import useStyles from "./Dropdown.style";

export const Dropdown = forwardRef<DropdownRef | null, CustomDropdownProps>(
  (
    {
      options,
      onChange,
      value,
      className,
      disabled = false,
      listTitle,
      placeholder,
      placeholderDisplayType,
      renderValue,
      children,
      contentClassName,
      onClose,
      isClosingOnSelect = true,
      onActionButtonClick,
      actionButtonLabel,
      onOpen,
      onOpenSync,
      forceOpen = false,
      forceClose = false,
      onButtonKeyDown,
      onElementKeyDown,
      required = false,
      inputProps,
      label,
      hasError,
      errorMessage,
      name,
      variant = "default",
      size = DropdownSize.DEFAULT,
      fullWidth = false,
      testId = 'default-test-id',
      autoTestId,
      helperText,
      autoFilterDescription,
    },
    ref
  ) => {
    const { getUniqueValue } = useUniqueWcagValues();
    const c = useStyles({ value, placeholder, variant, size });
    const [t] = useTranslation("translation");
    const [anchorEl, setAnchorEl] = useState<EventTarget | null>(null);
    const uniqueErrorId = getUniqueValue(`${name}-error`);
    const uniqueLabelId = getUniqueValue(`${name}-label`);
    const uniqueDescriptionId = autoFilterDescription ? getUniqueValue(`${name}-description`) : undefined;
    const selectRef = useRef();
    const contentWrapperRef = useRef(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const firstElementRef = useRef<HTMLLIElement>(null);
    const { ref: inputRef, isFocusVisible } = useFocusVisibleRef();
    useImperativeHandle(ref, () => ({
      buttonRef,
      firstElementRef,
    }));
    const actionButtonText = actionButtonLabel || t("filter");
    const {
      "aria-describedby": inputAriaDescribedBy,
      ...restInputProps
    } = inputProps || {};
    const ariaDescribedBy = mergeAriaDescribedBy(
      hasError && errorMessage ? uniqueErrorId : undefined,
      uniqueDescriptionId,
      inputAriaDescribedBy,
    );

    const handleActionButtonClick = (event: React.ChangeEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onActionButtonClick && onActionButtonClick(value);
      !forceOpen && setAnchorEl(null);
      onClose && onClose();
    };

    const handleOpenDropdown = (event: React.ChangeEvent<{}>) => {
      onOpenSync && onOpenSync();
      const { target } = event;
      if (onOpen) {
        onOpen().then(() => setAnchorEl(target));
      } else {
        !forceClose && setAnchorEl(target);
      }
    };

    const handleCloseDropdown = (event: React.ChangeEvent<{}>) => {
      if (event.currentTarget === contentWrapperRef.current) {
        return;
      }
      if (
        !isClosingOnSelect &&
        (event.currentTarget as HTMLElement).classList.contains(
          "MuiListItem-button"
        )
      ) {
        return;
      }
      setAnchorEl(null);
      onClose && onClose();
    };

    return (
      <FormControl className={c.formControl} fullWidth={fullWidth}>
        {label && (
          <FieldLabel id={uniqueLabelId} testId={uniqueLabelId}>
            {label}
          </FieldLabel>
        )}
        {autoFilterDescription && uniqueDescriptionId && (
          <VisuallyHidden id={uniqueDescriptionId}>
            {autoFilterDescription}
          </VisuallyHidden>
        )}
        <Select
          open={Boolean(anchorEl)}
          onOpen={handleOpenDropdown}
          onClose={handleCloseDropdown}
          error={hasError}
          name={name}
          IconComponent={DropdownIconIndicator}
          data-autotestid={autoTestId}
          renderValue={
            renderValue ||
            (() => (
              <DropdownDefaultValue
                options={options}
                value={value}
                placeholderDisplayType={placeholderDisplayType}
                placeholder={placeholder}
              />
            ))
          }
          disableUnderline
          displayEmpty
          ref={mergeRefs(selectRef, inputRef)}
          SelectDisplayProps={{
            "aria-describedby": ariaDescribedBy,
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            getContentAnchorEl: null,
            anchorEl: selectRef.current,
            PopoverClasses: {
              paper: c.popover,
            },
          }}
          data-testid={testId}
          className={clsx(
            c.root,
            isFocusVisible ? "Mui-focusVisible" : "",
            className
          )}
          classes={{
            icon: c.icon
          }}
          labelId={uniqueLabelId}
          value={value}
          disabled={disabled}
          onChange={onChange}
          inputProps={{
            "aria-required": required,
            ...restInputProps,
          }}
        >
          {children && (
            <div ref={contentWrapperRef} className={contentClassName}>
              {children}
            </div>
          )}
          {listTitle ? (
            <MenuItem className={c.placeholder} value="" disabled>
              {listTitle}
            </MenuItem>
          ) : null}
          {options.map((option: string | DropdownOptionModel, index: number) =>
            typeof option === "string" ? (
              <MenuItem
                key={option}
                value={option}
                className={c.menuItem}
                ref={index === 0 ? firstElementRef : null}
                onKeyDown={onElementKeyDown}
              >
                {option}
              </MenuItem>
            ) : (
              <MenuItem
                disabled={option.disabled}
                key={option.value}
                value={option.value}
                className={c.menuItem}
                ref={index === 0 ? firstElementRef : null}
                onKeyDown={onElementKeyDown}
              >
                <Typography component="span" className={c.menuItemLabelWrapper}>
                  {option.labelPrefix}
                  <Typography component="span" className={c.menuItemLabel} fontWeight='bold'>{option.label}</Typography>
                  {option.labelDescription && <Typography component="span" className={c.menuItemLabelDescription}>{option.labelDescription}</Typography>}
                </Typography>
              </MenuItem>
            )
          )}
          {onActionButtonClick && (
            <div className={c.actionButtonBox}>
              <Button
                onKeyDown={onButtonKeyDown}
                ref={buttonRef}
                onClick={handleActionButtonClick as () => void}
                fullWidth={true}
                buttonVariant='medium'
              >
                {actionButtonText}
              </Button>
            </div>
          )}
        </Select>
        {helperText && <FieldHelperText>{helperText}</FieldHelperText>}
        {hasError && errorMessage && (
          <FieldErrorMessage id={uniqueErrorId}>
            {errorMessage}
          </FieldErrorMessage>
        )}
      </FormControl>
    );
  }
);
